import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users, Mail, Tag, Star, Handshake, Loader2, Trash2, Plus, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TabButton({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
        active ? 'bg-[#C4A77D] text-white shadow-md' : 'bg-white text-[#595959] hover:bg-[#F4EDE6]'
      }`}
      data-testid={`admin-tab-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {count !== undefined && (
        <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${active ? 'bg-white/20' : 'bg-[#C4A77D]/10 text-[#C4A77D]'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

export default function AdminPage() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ members: [], contacts: [], partners: [], users: [] });

  // Discount code form
  const [codeForm, setCodeForm] = useState({ user_email: '', code: '', discount: '', description: '', valid_until: '' });
  // Points form
  const [pointsForm, setPointsForm] = useState({ user_email: '', points: '', reason: '' });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [members, contacts, partners, users] = await Promise.all([
        axios.get(`${API}/admin/members`),
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/partners`),
        axios.get(`${API}/admin/users`)
      ]);
      setData({
        members: members.data,
        contacts: contacts.data,
        partners: partners.data,
        users: users.data
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token && user?.is_admin) fetchAll();
    else setLoading(false);
  }, [token, user, fetchAll]);

  const handleCreateCode = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/discount-codes`, codeForm);
      toast.success('Rabattkod skapad!');
      setCodeForm({ user_email: '', code: '', discount: '', description: '', valid_until: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Något gick fel');
    }
  };

  const handleAddPoints = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/points`, { ...pointsForm, points: parseInt(pointsForm.points) });
      toast.success(`${pointsForm.points} poäng tillagda!`);
      setPointsForm({ user_email: '', points: '', reason: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Något gick fel');
    }
  };

  const handleDeleteCode = async (codeId) => {
    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`);
      toast.success('Rabattkod borttagen');
      fetchAll();
    } catch (err) {
      toast.error('Kunde inte ta bort rabattkoden');
    }
  };

  // Not admin
  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#F4EDE6] flex items-center justify-center" data-testid="admin-no-access">
        <div className="text-center p-8">
          <Shield className="w-16 h-16 text-[#C4A77D] mx-auto mb-6" />
          <h1 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">Ingen behörighet</h1>
          <p className="text-[#595959]">Du har inte behörighet att se denna sida.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4EDE6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C4A77D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EDE6]" data-testid="admin-page">
      {/* Header */}
      <section className="py-8 bg-[#1A1A1A]">
        <div className="container-main">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#C4A77D]" />
            <h1 className="font-['Avenir_Next'] text-2xl text-white">Admin Panel</h1>
          </div>
        </div>
      </section>

      <div className="container-main py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <TabButton active={activeTab === 'members'} onClick={() => setActiveTab('members')} icon={Users} label="Medlemmar" count={data.members.length} />
          <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')} icon={Users} label="Alla användare" count={data.users.length} />
          <TabButton active={activeTab === 'contacts'} onClick={() => setActiveTab('contacts')} icon={Mail} label="Meddelanden" count={data.contacts.length} />
          <TabButton active={activeTab === 'partners'} onClick={() => setActiveTab('partners')} icon={Handshake} label="Partneransökningar" count={data.partners.length} />
          <TabButton active={activeTab === 'discounts'} onClick={() => setActiveTab('discounts')} icon={Tag} label="Rabattkoder" />
          <TabButton active={activeTab === 'points'} onClick={() => setActiveTab('points')} icon={Star} label="Ge poäng" />
        </div>

        {/* Members Tab */}
        {activeTab === 'members' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl overflow-hidden" data-testid="admin-members-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F4EDE6]">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Namn</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">E-post</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Telefon</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Plan</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Status</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-[#8C8C8C]">Inga medlemmar ännu</td></tr>
                  ) : data.members.map((m, i) => (
                    <tr key={i} className="border-t border-[#F4EDE6]">
                      <td className="p-4 text-[#1A1A1A]">{m.name}</td>
                      <td className="p-4 text-[#595959]">{m.email}</td>
                      <td className="p-4 text-[#595959]">{m.phone || '-'}</td>
                      <td className="p-4"><span className="capitalize bg-[#C4A77D]/10 text-[#C4A77D] px-2 py-1 rounded text-sm">{m.plan}</span></td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-sm ${m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {m.status === 'active' ? 'Aktiv' : m.status === 'cancelled' ? 'Avslutad' : m.status}
                        </span>
                      </td>
                      <td className="p-4 text-[#8C8C8C] text-sm">{m.created_at ? new Date(m.created_at).toLocaleDateString('sv-SE') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl overflow-hidden" data-testid="admin-users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F4EDE6]">
                  <tr>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Namn</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">E-post</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Poäng</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Admin</th>
                    <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Registrerad</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u, i) => (
                    <tr key={i} className="border-t border-[#F4EDE6]">
                      <td className="p-4 text-[#1A1A1A]">{u.name}</td>
                      <td className="p-4 text-[#595959]">{u.email}</td>
                      <td className="p-4 text-[#C4A77D] font-medium">{u.points}</td>
                      <td className="p-4">{u.is_admin ? <Shield className="w-4 h-4 text-[#C4A77D]" /> : '-'}</td>
                      <td className="p-4 text-[#8C8C8C] text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString('sv-SE') : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="admin-contacts-list">
            {data.contacts.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-[#8C8C8C]">Inga meddelanden ännu</div>
            ) : data.contacts.map((c, i) => (
              <div key={i} className="bg-white rounded-2xl p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-medium text-[#1A1A1A]">{c.subject}</h3>
                    <p className="text-sm text-[#8C8C8C]">{c.name} &bull; {c.email}</p>
                  </div>
                  <span className="text-xs text-[#8C8C8C]">{c.created_at ? new Date(c.created_at).toLocaleString('sv-SE') : ''}</span>
                </div>
                <p className="text-[#595959]">{c.message}</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="admin-partners-list">
            {data.partners.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center text-[#8C8C8C]">Inga partneransökningar ännu</div>
            ) : data.partners.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-6">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-medium text-[#1A1A1A]">{p.company}</h3>
                    <p className="text-sm text-[#8C8C8C]">{p.contact} &bull; {p.email} {p.phone ? `&bull; ${p.phone}` : ''}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${p.status === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                    {p.status === 'new' ? 'Ny' : p.status}
                  </span>
                </div>
                {p.message && <p className="text-[#595959]">{p.message}</p>}
                <p className="text-xs text-[#8C8C8C] mt-2">{p.created_at ? new Date(p.created_at).toLocaleString('sv-SE') : ''}</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Discount Codes Tab */}
        {activeTab === 'discounts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <form onSubmit={handleCreateCode} className="bg-white rounded-2xl p-6" data-testid="create-discount-form">
              <h3 className="font-['Avenir_Next'] text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#C4A77D]" /> Skapa rabattkod
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Användarens e-post *</Label>
                  <Input value={codeForm.user_email} onChange={e => setCodeForm({...codeForm, user_email: e.target.value})} required className="mt-1 h-11 rounded-xl" data-testid="discount-user-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Kod *</Label>
                  <Input value={codeForm.code} onChange={e => setCodeForm({...codeForm, code: e.target.value})} required className="mt-1 h-11 rounded-xl" placeholder="t.ex. VALKOMST20" data-testid="discount-code" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Rabatt *</Label>
                  <Input value={codeForm.discount} onChange={e => setCodeForm({...codeForm, discount: e.target.value})} required className="mt-1 h-11 rounded-xl" placeholder="t.ex. 20%" data-testid="discount-value" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Giltig t.o.m.</Label>
                  <Input type="date" value={codeForm.valid_until} onChange={e => setCodeForm({...codeForm, valid_until: e.target.value})} className="mt-1 h-11 rounded-xl" data-testid="discount-valid" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Beskrivning</Label>
                  <Input value={codeForm.description} onChange={e => setCodeForm({...codeForm, description: e.target.value})} className="mt-1 h-11 rounded-xl" placeholder="t.ex. Välkomstrabatt för nya medlemmar" data-testid="discount-description" />
                </div>
              </div>
              <Button type="submit" className="mt-4 bg-[#C4A77D] hover:bg-[#B39669] text-white rounded-full h-11 px-6" data-testid="create-discount-btn">
                Skapa rabattkod
              </Button>
            </form>
          </motion.div>
        )}

        {/* Points Tab */}
        {activeTab === 'points' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <form onSubmit={handleAddPoints} className="bg-white rounded-2xl p-6" data-testid="add-points-form">
              <h3 className="font-['Avenir_Next'] text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-[#C4A77D]" /> Ge poäng till användare
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Användarens e-post *</Label>
                  <Input value={pointsForm.user_email} onChange={e => setPointsForm({...pointsForm, user_email: e.target.value})} required className="mt-1 h-11 rounded-xl" data-testid="points-user-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Antal poäng *</Label>
                  <Input type="number" value={pointsForm.points} onChange={e => setPointsForm({...pointsForm, points: e.target.value})} required className="mt-1 h-11 rounded-xl" data-testid="points-amount" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-[#8C8C8C]">Anledning</Label>
                  <Input value={pointsForm.reason} onChange={e => setPointsForm({...pointsForm, reason: e.target.value})} className="mt-1 h-11 rounded-xl" placeholder="t.ex. Välkomstbonus" data-testid="points-reason" />
                </div>
              </div>
              <Button type="submit" className="mt-4 bg-[#C4A77D] hover:bg-[#B39669] text-white rounded-full h-11 px-6" data-testid="add-points-btn">
                Ge poäng
              </Button>
            </form>

            {/* Users points overview */}
            <div className="bg-white rounded-2xl overflow-hidden mt-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F4EDE6]">
                    <tr>
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Namn</th>
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">E-post</th>
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-[#8C8C8C]">Poäng</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.users.filter(u => !u.is_admin).map((u, i) => (
                      <tr key={i} className="border-t border-[#F4EDE6]">
                        <td className="p-4 text-[#1A1A1A]">{u.name}</td>
                        <td className="p-4 text-[#595959]">{u.email}</td>
                        <td className="p-4 font-medium text-[#C4A77D]">{u.points}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
